/**
 * Error handling helpers.
 */

import type {
  ApiError,
  ValidationError,
  ApiErrorCode,
  ApiErrorResponse,
} from "@/types";

const ERROR_CODE_MESSAGES: Record<ApiErrorCode, string> = {
  NOT_LATEST_MESSAGE: "该消息不是最新消息，无法操作",
  MESSAGE_NOT_FOUND: "消息不存在，请刷新页面后重试",
  INVALID_MESSAGE_ROLE: "消息类型不匹配，操作失败",
};

const DETAIL_MESSAGES: Record<string, string> = {
  LOGIN_BAD_CREDENTIALS: "用户名、邮箱或密码错误",
  AUTH_SERVICE_UNAVAILABLE: "认证服务暂时不可用，请稍后重试",
  CURRENT_PASSWORD_INCORRECT: "当前密码错误",
};

export function extractErrorCode(error: unknown): ApiErrorCode | null {
  const axiosError = error as {
    response?: {
      data?: ApiErrorResponse;
    };
  };

  const code = axiosError.response?.data?.code;
  if (code && code in ERROR_CODE_MESSAGES) {
    return code;
  }
  return null;
}

export function getErrorMessageByCode(code: ApiErrorCode): string {
  return ERROR_CODE_MESSAGES[code];
}

export function extractErrorMessage(error: unknown): string {
  const axiosError = error as {
    response?: {
      data?: ApiError;
      status?: number;
    };
    message?: string;
  };

  if (axiosError.response?.data?.detail) {
    const detail = axiosError.response.data.detail;

    if (typeof detail === "string") {
      return DETAIL_MESSAGES[detail] || detail;
    }

    if (Array.isArray(detail)) {
      return detail
        .map((err: ValidationError) => `${err.loc.join(".")}: ${err.msg}`)
        .join("; ");
    }
  }

  if (axiosError.message) {
    return axiosError.message;
  }

  return "发生了未知错误，请重试";
}

export function isAuthError(error: unknown): boolean {
  const axiosError = error as {
    response?: {
      status?: number;
    };
  };

  return axiosError.response?.status === 401;
}

export function isForbiddenError(error: unknown): boolean {
  const axiosError = error as {
    response?: {
      status?: number;
    };
  };

  return axiosError.response?.status === 403;
}

export function isValidationError(error: unknown): boolean {
  const axiosError = error as {
    response?: {
      status?: number;
    };
  };

  return axiosError.response?.status === 422;
}

export function isNotFoundError(error: unknown): boolean {
  const axiosError = error as {
    response?: {
      status?: number;
    };
  };

  return axiosError.response?.status === 404;
}
